import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { schedule, scheduleOnce } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import $ from 'jquery';

import type { Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { scrollGuard } from 'webapp/utils/html';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Model } from 'webapp/components/tag-container';
import TagSelectComponent from 'webapp/components/tag-select';
import TagPopupTagsModalComponent from 'webapp/components/tag/popup-tags-modal';

import Board from 'webapp/models-custom/board';
import BoardLoop from 'webapp/models-custom/board-loop';
import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import CustomQuery from 'webapp/models-custom/custom-query';
import Goal from 'webapp/models-custom/goal';
import Report from 'webapp/models-custom/report';
import Script from 'webapp/models-custom/script';
import Space from 'webapp/models-custom/space';
import type Tag from 'webapp/models-custom/tag';

interface Args {
	model: Model;
	onContentChanged: () => void;
	onClose: () => void;
	onCreateTag?: () => void;
	onEditTags?: () => void;
	onApplySelected?: (selected: number[]) => void;
	theme?: string;
}

export default class TagPickerComponent extends Component<Args> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;

	@tracked newSelected: number[];

	private _tags: Tag[] = [];

	get theme(): string | null {
		return this.args.theme || null;
	}

	get isDirty(): boolean {
		const selected = this.initialTags,
			newSelected = this.newSelected;

		return (
			selected.length !== newSelected.length ||
			!selected.every((tag: number) => newSelected.includes(tag))
		);
	}

	get showApply(): boolean {
		return this.isDirty;
	}

	get initialTags() {
		return this.args.model.tags;
	}

	get allTags(): Tag[] {
		const tags = this.mmTagsService.savedRecords;

		if (this._tags.length !== tags.length) {
			// eslint-disable-next-line ember/no-side-effects
			this._tags = this.mmTagsService.createModelsFromRecordsArray(tags);
		}

		return this._tags;
	}

	// only for tracking
	get trackingObjectName(): string {
		const model = this.args.model;
		let obj = '';

		if (model instanceof Board) {
			obj = 'board';
		} else if (model instanceof Goal) {
			obj = 'goal';
		} else if (model instanceof Space) {
			obj = 'space';
		} else if (model instanceof Script) {
			obj = 'data calculation';
		} else if (model instanceof CustomQuery) {
			obj = 'custom query';
		} else if (model instanceof BoardLoop) {
			obj = 'loop';
		} else if (model instanceof Report) {
			obj = 'report';
		}

		return obj;
	}

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.newSelected = [...(this.initialTags || [])];
	}

	scrollListToBottom(): void {
		const ul = $('.tag-picker ul');

		if (ul.length) {
			ul.scrollTop(ul.get(0)!.scrollHeight);
		}
	}

	openTagModal(triggerAddNew = false): void {
		this.popupService
			.createModal(TagPopupTagsModalComponent, {
				popupClass: 'popup-type-tags-modal',
				allowStacked: true,
			})
			.open({
				triggerAddNew,
				onCreatedTagsSaved: this.addTagsToNewSelection,
			});
		this.analyticsService.event(TrackingEvent.TAGS_MANAGE);
	}

	@action
	addTagsToNewSelection(tags: Tag[]): void {
		tags.forEach((tag) => {
			this.addToNewSelected(tag.numericId!);
		});
		this.applySelected(false);
	}

	@action
	addToNewSelected(tagId: number): void {
		if (!this.newSelected.includes(tagId)) {
			this.newSelected = [...this.newSelected, tagId];
		}
	}

	@action
	removeFromNewSelected(tagId: number): void {
		this.newSelected = this.newSelected.filter((id) => id !== tagId);
	}

	@action
	setupView(): void {
		schedule('afterRender', () => {
			scrollGuard($('.tag-picker ul'));
		});
	}

	@action
	contentChange(): void {
		scheduleOnce('afterRender', this, this.args.onContentChanged);
		scheduleOnce('afterRender', this, this.scrollListToBottom);
	}

	@action
	applySelectedEvent() {
		this.applySelected(true);
	}

	applySelected(closeOnFinish = true): void {
		if (this.args.onApplySelected) {
			this.args.onApplySelected(this.newSelected);
		} else {
			const model = this.args.model;
			model.tags = this.newSelected;

			let savePromise: Promise<unknown> = Promise.resolve();
			if (model instanceof Space) {
				// special endpoint to edit client tags
				const payload = { tagIds: model.tags };
				savePromise = this.requestService
					.apiWithAsyncReply('PUT', `/tags/account/${model.id}`, payload)
					.then(() => {
						model.directPropUpdate({ tags: model.tags });
					});
			} else if (model instanceof BufferedModel) {
				savePromise = model.save();
			}

			savePromise.catch(
				this.requestService.showAllValidationErrors.bind(this.requestService),
			);
		}

		this.analyticsService.event(TrackingEvent.TAGS_ASSIGNED, {
			object: this.trackingObjectName,
		});

		if (this.args.onClose && closeOnFinish) {
			this.args.onClose();
		}
	}

	@action
	createTag(): void {
		this.openTagModal(true);
	}

	@action
	editTags(): void {
		this.openTagModal();
	}
	<template>
		<div
			class="context-ct tag-picker"
			{{didInsert this.setupView}}
			{{didUpdate this.contentChange this.allTags}}
		>
			<div class="title">Tag as</div>
			<a role="button" class="close" {{on "click" @onClose}}>
				<CommonUiIconComponent @name="Close" @size="sm" />
			</a>
			<ul class="scroll">
				{{#each this.allTags as |tag|}}
					<TagSelectComponent
						@tag={{tag}}
						@selected={{this.initialTags}}
						@newSelected={{this.newSelected}}
						@theme={{this.theme}}
						@onAddToNewSelected={{this.addToNewSelected}}
						@onRemoveFromNewSelected={{this.removeFromNewSelected}}
					/>
				{{else}}
					<li>No tags created yet.</li>
				{{/each}}
			</ul>
			{{#if this.showApply}}
				<a role="button" class="apply" {{on "click" this.applySelectedEvent}}>Save</a>
			{{else}}
				<a role="button" class="new" {{on "click" this.createTag}}>New tag</a>
				<a role="button" class="edit" {{on "click" this.editTags}}>Manage tags</a>
			{{/if}}
		</div>
	</template>
}
